import { FC } from 'react';

import { Flex, Form, Select, Typography, Row, Col } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';

import { StorageType, STORAGE_TYPE_LABELS } from '@/constants';

const { Text } = Typography;

interface SelectMetricProps {
  form: FormInstance;
}

const SelectMetric: FC<SelectMetricProps> = ({ form }) => {
  const { t } = useTranslation();

  const handleAssetsChange = (values: string[]) => {
    if (values.length > 2) {
      const newValues = values.slice(0, 2);
      form.setFieldValue('selectedAssets', newValues);
      return;
    }
    form.setFieldValue('selectedAssets', values);
  };

  return (
    <Flex vertical justify="space-between" gap="large">
      <Row gutter={[16, 24]} align="middle">
        <Col>
          <Flex align="center" justify="center" gap={5}>
            <Text strong style={{ color: '#ff4d4f' }}>
              *
            </Text>
            {t('components.simReport.selectMetric.benchmarkByUsing')}{' '}
          </Flex>
        </Col>

        <Col>
          <Form.Item
            name="selectedMetric"
            rules={[
              {
                required: true,
                message: t(
                  'components.simReport.selectMetric.metric.requiredMessage'
                ),
              },
            ]}
            style={{ margin: 0, minWidth: 120 }}
          >
            <Select
              placeholder={t(
                'components.simReport.selectMetric.metric.placeholder'
              )}
              size="small"
            >
              <Select.Option value="irr">
                {t('components.simReport.selectMetric.options.irr')}
              </Select.Option>
              <Select.Option value="grossProfit">
                {t('components.simReport.selectMetric.options.grossProfit')}
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="selectedStorageType"
            rules={[
              {
                required: true,
                message: t(
                  'components.simReport.selectMetric.storageType.requiredMessage'
                ),
              },
            ]}
            style={{ margin: 0, minWidth: 150 }}
          >
            <Select
              placeholder={t(
                'components.simReport.selectMetric.storageType.placeholder'
              )}
              size="small"
            >
              {Object.entries(StorageType).map(([, value]) => (
                <Select.Option key={value} value={value}>
                  {t(STORAGE_TYPE_LABELS[value])}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={24}>
          <Flex align="center" gap={8}>
            <Flex align="center" justify="center" gap={5}>
              <Text strong style={{ color: '#ff4d4f' }}>
                *
              </Text>
              {t(
                'components.simReport.selectMetric.selectAssetsForComparison'
              )}{' '}
            </Flex>
            <Form.Item
              name="selectedAssets"
              rules={[
                {
                  required: true,
                  message: t(
                    'components.simReport.selectMetric.assets.requiredMessage'
                  ),
                },
              ]}
              style={{ margin: 0, minWidth: 220 }}
            >
              <Select
                mode="multiple"
                placeholder={t(
                  'components.simReport.selectMetric.assets.placeholder'
                )}
                onChange={handleAssetsChange}
                maxTagCount={2}
                size="small"
              >
                <Select.Option value="plant">
                  {t('components.simReport.selectMetric.options.plant')}
                </Select.Option>
                <Select.Option value="scenario">
                  {t('components.simReport.selectMetric.options.scenario')}
                </Select.Option>
                <Select.Option value="storageSystem">
                  {t('components.simReport.selectMetric.options.storageSystem')}
                </Select.Option>
              </Select>
            </Form.Item>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t(
                'components.simReport.selectMetric.assets.maxSelectionMessage'
              )}
            </Text>
          </Flex>
        </Col>
      </Row>
    </Flex>
  );
};

export default SelectMetric;
